import { z } from "zod";

import { DeviceTypeList } from "../schemas/analytics/base";

import { AudienceDtoSchema } from "./audiences";
import { DiscountAppDtoSchema } from "./discount-apps";
import { ExperienceActionDtoSchema } from "./experience-actions";
import {
  CreateExperienceAnalysisSchema,
  ExperienceAnalysisDtoSchema,
} from "./experience-analysis";
import {
  CreateExperienceIntegrationDtoSchema,
  ExperienceIntegrationDtoSchema,
} from "./experience-integrations";
import { ExperiencePageTargetingDtoSchema } from "./experience-page-targeting";
import { ExperienceProductDtoSchema } from "./experience-products";
import { OfferProductDtoSchema } from "./offer-products";
import { S3FileLocationDtoSchema } from "./s3-file-location";
import { ShippingTestMethodDefinitionDtoSchema } from "./shipping-test-method-definitions";
import { UserInterfaceDtoSchema } from "./user-interfaces";
import { VariationDtoSchema } from "./variations";

export const ExperienceDevicePreviewDtoSchema = z.object({
  device: z.enum(DeviceTypeList).default("desktop"),
  path: z.string().default("/"),
});
export type ExperienceDevicePreviewDto = z.infer<
  typeof ExperienceDevicePreviewDtoSchema
>;

export type HasExperienceTypeProperty =
  | "hasTestPricing"
  | "hasTestShipping"
  | "hasTestDiscount";

export const ExperienceStatusList = [
  "pending",
  "started",
  "ended",
  "paused",
] as const;
export type ExperienceStatus = (typeof ExperienceStatusList)[number];

export const ExperienceTypeList = [
  "content/advanced",
  "content/onsiteEdits",
  "content/url",
  "content/theme",
  "content/template",
  "pricing",
  "shipping",
  "personalization",
  "personalization/offer",
  "sitewide",
] as const;
export type ExperienceType = (typeof ExperienceTypeList)[number];

export const ExperienceCategoryList = [
  "experiment",
  "personalization",
] as const;
export type ExperienceCategory = (typeof ExperienceCategoryList)[number];

export const ExperienceCurrencyDtoSchema = z.object({
  code: z.string().default("USD"),
  country: z.string().optional(),
  options: z.any(),
  symbol: z.string().default("$"),
  suffix: z.string().optional(),
});

export type ExperienceCurrencyDto = z.infer<typeof ExperienceCurrencyDtoSchema>;

export const ExperienceTestTypeDtoSchema = z.object({
  hasTestPricing: z.boolean(),
  hasTestShipping: z.boolean(),
  hasTestCampaign: z.boolean(),
  hasTestContent: z.boolean(),
  hasTestContentUrl: z.boolean(),
  hasTestContentAdvanced: z.boolean(),
  hasTestContentOnsite: z.boolean(),
  hasTestContentTemplate: z.boolean(),
  hasTestContentTheme: z.boolean(),
  hasTestOnsiteInjections: z.boolean(),
});

export type ExperienceTestTypeDto = z.infer<typeof ExperienceTestTypeDtoSchema>;
export const ExperienceDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isPreview: z.boolean().optional().default(true),
  description: z.string().nullish(),
  devicePreview: ExperienceDevicePreviewDtoSchema.nullish(),
  organizationId: z.string().uuid(),
  type: z.enum(ExperienceTypeList),
  category: z.enum(ExperienceCategoryList),
  status: z.enum(ExperienceStatusList),
  testTypes: ExperienceTestTypeDtoSchema,
  experiencePageTargeting: ExperiencePageTargetingDtoSchema.array()
    .optional()
    .default([]),
  experienceActions: ExperienceActionDtoSchema.array(),
  experienceAnalysis: ExperienceAnalysisDtoSchema,
  experienceIntegrations: ExperienceIntegrationDtoSchema.array().default([]),
  offerProducts: OfferProductDtoSchema.array().optional().default([]),
  experienceProducts: ExperienceProductDtoSchema.array().nullish().default([]),
  currency: ExperienceCurrencyDtoSchema.nullish(),
  variations: VariationDtoSchema.array().default([]),
  createdAtTs: z.number(),
  isDeleted: z.boolean().optional(),
  discountApp: DiscountAppDtoSchema.nullish(),
  shippingTestMethodDefinitions:
    ShippingTestMethodDefinitionDtoSchema.array().default([]),
  audience: AudienceDtoSchema.nullish(),
  userInterfaces: UserInterfaceDtoSchema.array().default([]),
  lastUpdateTs: z.number(),
  previewPath: z.string().nullish(),
  isExternal: z.boolean().default(false).nullish(),
  startedAtTs: z.number().nullable(),
  pausedAtTs: z.number().nullable(),
  requiresLink: z.boolean().optional().default(false),
  linkBaseUrl: z.string().nullable(),
  endedAtTs: z.number().nullable(),
  productVariantCount: z.number().default(0),
});

export type ExperienceDto = z.infer<typeof ExperienceDtoSchema>;

export const CreateExperienceDtoSchema = ExperienceDtoSchema.omit({
  experienceActions: true,
  experiencePageTargeting: true,
  offerProducts: true,
  experienceProducts: true,
  variations: true,
  audience: true,
  userInterfaces: true,
  createdAtTs: true,
  organizationId: true,
  lastUpdateTs: true,
}).extend({
  id: z.string().uuid().optional(),
  productsLocation: S3FileLocationDtoSchema.nullish(),
  experienceIntegrations: CreateExperienceIntegrationDtoSchema.array().default(
    []
  ),
  experienceAnalysis: CreateExperienceAnalysisSchema,
});

export type CreateExperienceDto = z.infer<typeof CreateExperienceDtoSchema>;

export const PatchExperienceDtoSchema = z.object({
  name: z.string().optional(),
  isPreview: z.boolean().optional(),
  description: z.string().optional(),
  isDeleted: z.boolean().optional(),
  devicePreview: ExperienceDevicePreviewDtoSchema.optional(),
});

export type PatchExperienceDto = z.infer<typeof PatchExperienceDtoSchema>;

export type HasExperienceDto = {
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTheme: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentUrl: boolean;
  hasTestOnsiteInjections: boolean;
};
